import {
  apiCall,
  PlaybookApiError,
  type ApiClientConfig,
} from "@playbookai/api-clients";
import type { KpiTileRange } from "@playbookai/widget-schemas";

/**
 * Resolve ONE KPI Manager KPI for a widget that displays it — the data half of
 * `@playbookai/ui`'s `useKpiSource()` seam.
 *
 * It lives here, in a non-feature package, for the reason this package exists:
 * SOLVE's canvas, the dashboards and any later host all need the same
 * resolution, and a feature package may never import a sibling feature package,
 * so `@playbookai/kpi-feature`'s own hooks are not reachable from the SOLVE
 * canvas. Nothing here re-derives KPI semantics: the value, its trust, the
 * applicable target and the ledger all come from the KPI's own endpoints.
 */

const seg = encodeURIComponent;

/** Only the fields the tile actually renders; the read returns more. */
interface KpiDetailWire {
  readonly kpiId: string;
  readonly name: string;
  readonly unit: string;
  readonly direction: "HIGHER_IS_BETTER" | "LOWER_IS_BETTER" | "TARGET_RANGE";
  readonly lifecycleStatus: "draft" | "active" | "archived";
  readonly reportingFrequency: string | null;
  readonly currentTarget: {
    readonly targetValue: string;
    readonly targetDate: string | null;
  } | null;
  readonly currentState: {
    readonly measurementStatus: string;
    readonly currentValue: string | null;
    readonly observationTime: string | null;
  } | null;
}

interface KpiObservationWire {
  readonly observationId: string;
  readonly value: string;
  readonly observedAt: string;
}

interface KpiObservationPageWire {
  readonly data: readonly KpiObservationWire[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

const DAY_MS = 86_400_000;
/** The windows the tile offers, resolved the way KPI Manager's own page does. */
const RANGE_DAYS: Record<KpiTileRange, number | null> = {
  "30d": 30,
  "90d": 90,
  all: null,
};

/**
 * The server pages at 100 and a sparkline wants the whole window, so the read
 * sweeps — bounded, so a bad cursor cannot spin and an enormous history cannot
 * lock the tab up. Hitting the cap THROWS rather than returning a partial
 * series: a chart silently missing its oldest half is worse than a visible
 * failure, because nothing on screen says it is incomplete. Same rule, and the
 * same numbers, as KPI Manager's own `useKpiObservations`.
 */
const MAX_PAGES = 20;
const PAGE_LIMIT = 100;

export interface KpiSourceReading {
  readonly observedAt: string;
  readonly value: number;
}

export interface ResolvedKpiSource {
  readonly kpiId: string;
  readonly name: string;
  readonly unit: string;
  readonly higherIsBetter: boolean | null;
  readonly targetRange?: boolean;
  readonly currentValue: number | null;
  /** Stored decimal text for exact scoring; numeric values are for display/charts. */
  readonly currentValueExact?: string | null;
  readonly observedAt: string | null;
  /** KPI Manager's own `MeasurementStatus`; null when there is no projection. */
  readonly measurementStatus:
    | "not_connected"
    | "current"
    | "stale"
    | "unavailable"
    | null;
  readonly target: number | null;
  readonly targetExact?: string | null;
  readonly targetDate: string | null;
  readonly readings: readonly KpiSourceReading[];
  readonly archived: boolean;
  readonly reportingFrequency: string | null;
}

/** A number the ledger stores as a decimal STRING; null when it is not one. */
function toNumber(raw: string | null | undefined): number | null {
  if (raw == null || raw.trim() === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

async function sweepObservations(
  config: ApiClientConfig,
  kpiId: string,
  from: string | undefined,
  signal: AbortSignal | undefined,
): Promise<readonly KpiObservationWire[]> {
  const rows: KpiObservationWire[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // The signal rides EVERY page: a sweep is one logical request, so
    // cancelling it must stop the rest rather than orphan them.
    const res = await apiCall<KpiObservationPageWire>(
      config,
      "GET",
      `/v1/kpis/${seg(kpiId)}/observations`,
      undefined,
      {
        ...(from ? { from } : {}),
        limit: PAGE_LIMIT,
        ...(cursor ? { cursor } : {}),
      },
      signal,
    );
    rows.push(...res.data);
    if (!res.hasMore) return rows;
    if (!res.nextCursor) {
      throw new Error(
        "KPI observation pagination indicated more results without a next cursor.",
      );
    }
    cursor = res.nextCursor;
  }
  throw new Error(
    `KPI observation history exceeded ${MAX_PAGES} pages; refusing to draw a partial series.`,
  );
}

/**
 * `forbidden` is its own outcome rather than an error.
 *
 * A KPI the reader may not see and a KPI that is gone are different facts about
 * the workspace, and the tile states them differently. The API answers 404 for
 * another tenant's KPI by design, so "not found" is the safe collapse of both
 * "deleted" and "not yours" — 403 is the narrower, explicit refusal.
 */
export class KpiSourceForbidden extends Error {
  constructor() {
    super("The signed-in user may not read this KPI.");
    this.name = "KpiSourceForbidden";
  }
}

export class KpiSourceUnavailable extends Error {
  constructor(message = "This KPI is no longer available.") {
    super(message);
    this.name = "KpiSourceUnavailable";
  }
}

/**
 * Read a KPI and the readings inside one window.
 *
 * The two reads are issued TOGETHER: the detail alone cannot draw a trend and
 * the ledger alone cannot name the KPI, so serialising them would only add a
 * round trip to every tile on a canvas.
 */
export async function fetchKpiSource(
  config: ApiClientConfig,
  kpiId: string,
  range: KpiTileRange,
  signal?: AbortSignal,
): Promise<ResolvedKpiSource> {
  const days = RANGE_DAYS[range];
  const from =
    days === null
      ? undefined
      : new Date(Date.now() - days * DAY_MS).toISOString();
  try {
    const [detail, observations] = await Promise.all([
      apiCall<KpiDetailWire>(
        config,
        "GET",
        `/v1/kpis/${seg(kpiId)}`,
        undefined,
        undefined,
        signal,
      ),
      sweepObservations(config, kpiId, from, signal),
    ]);
    const state = detail.currentState;
    return {
      kpiId: detail.kpiId,
      name: detail.name,
      unit: detail.unit,
      higherIsBetter:
        detail.direction === "TARGET_RANGE"
          ? null
          : detail.direction === "HIGHER_IS_BETTER",
      targetRange: detail.direction === "TARGET_RANGE",
      // The projection suppresses both the value and the RAG unless the
      // measurement is `current`, so a non-current status has no trusted value
      // to show — that is the platform's judgement, not a display choice.
      currentValue:
        state?.measurementStatus === "current"
          ? toNumber(state.currentValue)
          : null,
      currentValueExact:
        state?.measurementStatus === "current" ? state.currentValue : null,
      observedAt: state?.observationTime ?? null,
      // Passed through, never re-derived: the tile says what the platform says
      // about its own source instead of inventing a freshness rule.
      measurementStatus:
        (state?.measurementStatus as ResolvedKpiSource["measurementStatus"]) ??
        null,
      target: toNumber(detail.currentTarget?.targetValue),
      targetExact: detail.currentTarget?.targetValue ?? null,
      targetDate: detail.currentTarget?.targetDate ?? null,
      // The server keysets DESCENDING; a chart reads left to right, so the
      // reversal happens ONCE here and everything downstream may assume
      // ascending by `observedAt`.
      readings: observations
        .map((row) => ({
          observedAt: row.observedAt,
          value: toNumber(row.value),
        }))
        .filter((row): row is KpiSourceReading => row.value !== null)
        .reverse(),
      archived: detail.lifecycleStatus === "archived",
      reportingFrequency: detail.reportingFrequency,
    };
  } catch (error) {
    if (error instanceof PlaybookApiError && error.status === 403) {
      throw new KpiSourceForbidden();
    }
    if (error instanceof PlaybookApiError && error.status === 404) {
      throw new KpiSourceUnavailable();
    }
    throw error;
  }
}

export const kpiSourceKey = (
  kpiId: string,
  range: KpiTileRange,
  authScope: readonly unknown[],
): readonly unknown[] => ["kpi-source", ...authScope, kpiId, range];
